// ═══════════════════════════════════════════════════════════════
// frontend/src/pages/PartnerList.tsx
// ═══════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PartnerApi } from "../lib/api";
import { Link, useNavigate } from "react-router-dom";

export default function PartnerList() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: partners, isLoading, error } = useQuery({ queryKey: ["partners"], queryFn: () => PartnerApi.list() });

  const deleteMutation = useMutation({
    mutationFn: PartnerApi.delete,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["partners"] }),
  });

  if (isLoading) return <p className="text-gray-500">Loading partners...</p>;
  if (error) return <p className="text-red-500">Error: {(error as Error).message}</p>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Partners</h1>
        <Link to="/partners/new" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          + New Partner
        </Link>
      </div>
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Phone</th>
              <th className="text-left px-4 py-3 font-medium text-gray-600">Type</th>
              <th className="text-right px-4 py-3 font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {partners?.map((p: any) => (
              <tr key={p.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`/partners/${p.id}`)}>
                <td className="px-4 py-3 font-medium">{p.name}</td>
                <td className="px-4 py-3 text-gray-600">{p.email ?? "—"}</td>
                <td className="px-4 py-3 text-gray-600">{p.phone ?? "—"}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    p.is_company ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700"
                  }`}>
                    {p.is_company ? "Company" : "Individual"}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteMutation.mutate(p.id); }}
                    className="text-red-600 hover:text-red-800 text-xs"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
            {(!partners || partners.length === 0) && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-400">No partners found</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
